import React from "react";
import { Badge, Box, IconButton, Typography } from "@mui/material";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/styles";
import EquipmentRowName from "components/EquipmentRowName";
import { EquipmentRowTechnicalCondition } from "components/EquipmentRowTechnicalCondition";
import Tooltiper from "components/Tooltiper";
import DateViewer from "components/DateViewer";
import { SportEquipmentEntity, TechnicalCondition } from "graphql/graphQlApiHooks";
import { BootstrapTooltip } from "../../../../../../components/BootstrapTooltip";
import { ActionButtons } from "./ActionButtons";

interface NameCellProps {
  row: SportEquipmentEntity;
}

export const NameCell = React.memo(function NameCell({ row }: NameCellProps) {
  const {
    name,
    inventoryNumber,
    technicalCondition,
    photos,
    mainPhoto,
    inUse,
    failureReason,
    isEditable,
    isInformationComplete
  } = row;

  // Вычисляем avatarUrl и avatarUrlOrigin один раз
  const avatarUrl = mainPhoto ? mainPhoto.avatarUrl : (photos && photos?.length > 0) ? photos?.[0].avatarUrl : undefined;
  const avatarUrlOrigin = mainPhoto ? mainPhoto.url : (photos && photos?.length > 0) ? photos?.[0].url : undefined;

  return (
    <EquipmentRowName
      name={name}
      inventoryNumber={inventoryNumber || "б/н"}
      inUse={inUse}
      technicalCondition={technicalCondition === TechnicalCondition.Working}
      avatarUrl={avatarUrl}
      avatarUrlOrigin={avatarUrlOrigin}
      failureReason={failureReason}
      isEditable={isEditable}
      isInformationComplete={isInformationComplete}
    />
  );
}, (prevProps, nextProps) => {
  // Кастомное сравнение - проверяем только изменённые поля
  const prev = prevProps.row;
  const next = nextProps.row;
  
  return (
    prev.id === next.id &&
    prev.name === next.name &&
    prev.inventoryNumber === next.inventoryNumber &&
    prev.technicalCondition === next.technicalCondition &&
    prev.inUse === next.inUse &&
    prev.failureReason === next.failureReason &&
    prev.isEditable === next.isEditable &&
    prev.isInformationComplete === next.isInformationComplete &&
    prev.mainPhoto?.id === next.mainPhoto?.id &&
    prev.mainPhoto?.avatarUrl === next.mainPhoto?.avatarUrl &&
    prev.mainPhoto?.url === next.mainPhoto?.url &&
    prev.photos?.length === next.photos?.length &&
    (prev.photos?.length === 0 || prev.photos?.[0]?.id === next.photos?.[0]?.id) &&
    (prev.photos?.length === 0 || prev.photos?.[0]?.avatarUrl === next.photos?.[0]?.avatarUrl) &&
    (prev.photos?.length === 0 || prev.photos?.[0]?.url === next.photos?.[0]?.url)
  );
});

interface TechnicalConditionCellProps {
  row: SportEquipmentEntity;
}

export const TechnicalConditionCell = React.memo(function TechnicalConditionCell({ row }: TechnicalConditionCellProps) {
  return (
    <EquipmentRowTechnicalCondition
      technicalCondition={row.technicalCondition === TechnicalCondition.Working}
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.row.technicalCondition === nextProps.row.technicalCondition
  );
});

interface SportZoneCellProps {
  row: SportEquipmentEntity;
  tabActiveId?: string;
}

export const SportZoneCell = React.memo(function SportZoneCell({ row, tabActiveId }: SportZoneCellProps) {
  const theme = useTheme();
  const { sportZone, sportObject, inUse } = row;

  return (
    <Tooltiper
      text={sportZone?.name ?
        <b>{sportZone?.name}</b> : (tabActiveId === "is_archived") ?
          <><Typography
            sx={{ opacity: .5 }}
            mr={1}
            variant={"body2"}
            component={"span"}
          >На складе
            в</Typography><b>{sportObject?.name}</b></> :
          "Здание/Помещение не известно"
      }
      props={{
        variant: !sportZone?.name ? "body2" : "subtitle1",
        style: {
          ...(!inUse && { color: theme.palette.grey["500"] })
        }
      }}
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.row.sportZone?.id === nextProps.row.sportZone?.id &&
    prevProps.row.sportZone?.name === nextProps.row.sportZone?.name &&
    prevProps.row.sportObject?.id === nextProps.row.sportObject?.id &&
    prevProps.row.sportObject?.name === nextProps.row.sportObject?.name &&
    prevProps.row.inUse === nextProps.row.inUse &&
    prevProps.tabActiveId === nextProps.tabActiveId
  );
});

interface SportSubZoneCellProps {
  row: SportEquipmentEntity;
  tabActiveId?: string;
}

export const SportSubZoneCell = React.memo(function SportSubZoneCell({ row, tabActiveId }: SportSubZoneCellProps) {
  const theme = useTheme();
  const { sportSubZone, sportObject, inUse } = row;
  const sportSubZoneName = sportSubZone?.name;

  return (
    <Tooltiper
      text={sportSubZoneName ?
        <b>{sportSubZoneName}</b> : (tabActiveId === "is_archived") ?
          <><Typography
            sx={{ opacity: .5 }}
            mr={1}
            variant={"body2"}
            component={"span"}
          >На складе
            в</Typography><b>{sportObject?.name}</b></> :
          "Спортзона не известена"
      }
      props={{
        variant: sportSubZoneName ? "subtitle1" : "body2",
        style: {
          ...(!inUse && { color: theme.palette.grey["500"] })
        }
      }}
    />
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.row.sportSubZone?.id === nextProps.row.sportSubZone?.id &&
    prevProps.row.sportSubZone?.name === nextProps.row.sportSubZone?.name &&
    prevProps.row.sportObject?.id === nextProps.row.sportObject?.id &&
    prevProps.row.sportObject?.name === nextProps.row.sportObject?.name &&
    prevProps.row.inUse === nextProps.row.inUse &&
    prevProps.tabActiveId === nextProps.tabActiveId
  );
});

interface DateCellProps {
  row: SportEquipmentEntity;
  field: 'commissioningDate' | 'createdAt';
}

export const DateCell = React.memo(function DateCell({ row, field }: DateCellProps) {
  const date = row[field];
  const inUse = row.inUse;

  return (
    <Box color={inUse ? "text.secondary" : "grey.500"}>
      <DateViewer date={date} />
    </Box>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.row[prevProps.field] === nextProps.row[nextProps.field] &&
    prevProps.row.inUse === nextProps.row.inUse
  );
});

interface ServicePeriodsCellProps {
  row: SportEquipmentEntity;
  onNavigate: (id: string) => void;
}

export const ServicePeriodsCell = React.memo(function ServicePeriodsCell({ row, onNavigate }: ServicePeriodsCellProps) {
  const disabled = !row.sportSubZone?.id;

  return (
    <IconButton
      size={"small"}
      onClick={() => {
        onNavigate(row.id);
      }}
      disabled={disabled}
    >
      <Badge
        badgeContent={row.equipmentServicePeriods?.length}
        color={disabled ? "default" : "primary"}
        variant='dot'
      >
        <BootstrapTooltip title={"График технического обслуживания"}>
          <EventNoteOutlinedIcon
            fontSize='small'
            sx={{ fontSize: "18px !important" }}
          />
        </BootstrapTooltip>
      </Badge>
    </IconButton>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row.id === nextProps.row.id &&
    prevProps.row.sportSubZone?.id === nextProps.row.sportSubZone?.id &&
    prevProps.row.equipmentServicePeriods?.length === nextProps.row.equipmentServicePeriods?.length &&
    prevProps.onNavigate === nextProps.onNavigate
  );
});

interface ActionsCellProps {
  row: SportEquipmentEntity;
}

export const ActionsCell = React.memo(function ActionsCell({ row }: ActionsCellProps) {
  return (
    <ActionButtons
      sportEquipment={row}
    />
  );
}, (prevProps, nextProps) => {
  // Для ActionsCell проверяем только id, так как ActionButtons сам управляет своим состоянием
  return prevProps.row.id === nextProps.row.id;
});


